import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'
import { fetchAllRows } from '@/lib/supabase/fetch-all-rows'
import { ilikeAnyColumn, searchWords } from '@/lib/supabase/search-filter'
import type { Generation } from './generations'
import type { Tag } from './tags'

// =============================================================================
// Product Types
// =============================================================================

type ProductRow = Omit<
  Database['public']['Tables']['products']['Row'],
  'attributes'
> & {
  /** Catalogue attributes, mirrored from the linked catalogue product */
  attributes: Record<string, string>
}

export interface ProductFilterOptions {
  colorways: string[]
  collections: string[]
  genders: string[]
  main_groups: string[]
  sub_groups: string[]
  categories: string[]
  /** Catalogue attribute name → its values in the workspace */
  attributes: Record<string, string[]>
}

/**
 * Product with current generation and author profile.
 * Products are domain entities; generations are their versions/transformations.
 * current_generation embeds the full Generation including its author profile.
 */
export interface Product extends ProductRow {
  profiles: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
  approval_profile: {
    id: string
    full_name: string | null
  } | null
  current_generation: Generation | null
  product_tags: { tags: Tag }[]
}

/**
 * Product with version count (for grid display)
 */
export interface ProductWithVersionCount extends Product {
  version_count: number
}

const PAGE_SIZE = 16
const MIN_SEARCH_WORD_LENGTH = 2

export interface ProductFilters {
  authorId?: string | null
  searchQuery?: string | null
  tagIds?: string[] | null
  colorway?: string | null
  collection?: string | null
  gender?: string | null
  mainGroup?: string | null
  subGroup?: string | null
  category?: string | null
  /** Catalogue attributes that must all match */
  attributes?: Record<string, string> | null
  approvalStatus?: 'approved' | 'denied' | 'pending' | null
}

export type CompletedProductFilters = Pick<
  ProductFilters,
  'approvalStatus' | 'searchQuery'
>

/**
 * Build base product select query with standard joins.
 * Encapsulates: table, select columns, workspace filter, soft-delete filter, ordering.
 */
function buildProductSelectQuery(
  workspaceId: string,
  options?: { count?: 'exact' }
) {
  return supabase
    .from('products')
    .select(
      `*,
      profiles!author(id, email, full_name, avatar_url),
      approval_profile:profiles!approved_by(id, full_name),
      current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
      product_tags(tags(id, name, color))`,
      options
    )
    .eq('workspace_id', workspaceId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
}

/**
 * Apply product filters to any Supabase query builder.
 * Works with both select and update queries.
 */
function applyProductFilters<
  T extends {
    eq: (col: string, val: string) => T
    or: (filter: string) => T
    is: (col: string, val: null) => T
    contains: (col: string, val: Record<string, string> | string[]) => T
  },
>(query: T, filters?: ProductFilters): T {
  let q = query

  if (filters?.authorId) {
    q = q.eq('author', filters.authorId)
  }

  // Every selected label must be present.
  if (filters?.tagIds && filters.tagIds.length > 0) {
    q = q.contains('tag_ids', filters.tagIds)
  }

  if (filters?.searchQuery) {
    for (const word of searchWords(
      filters.searchQuery,
      MIN_SEARCH_WORD_LENGTH
    )) {
      q = q.or(ilikeAnyColumn(['name', 'article_number', 'colorway'], word))
    }
  }

  if (filters?.colorway) {
    q = q.eq('colorway', filters.colorway)
  }

  if (filters?.collection) {
    q = q.eq('collection', filters.collection)
  }

  if (filters?.gender) {
    q = q.eq('gender', filters.gender)
  }

  if (filters?.mainGroup) {
    q = q.eq('main_group', filters.mainGroup)
  }

  if (filters?.subGroup) {
    q = q.eq('sub_group', filters.subGroup)
  }

  if (filters?.category) {
    q = q.eq('category', filters.category)
  }

  if (filters?.attributes && Object.keys(filters.attributes).length > 0) {
    q = q.contains('attributes', filters.attributes)
  }

  if (filters?.approvalStatus) {
    if (filters.approvalStatus === 'pending') {
      q = q.is('approval_status', null)
    } else {
      q = q.eq('approval_status', filters.approvalStatus)
    }
  }

  return q
}

/**
 * Page result from infinite query, includes total count.
 */
export interface ProductPage {
  products: Product[]
  totalCount: number
}

export const product = {
  findOne: (productId: string) =>
    queryOptions({
      queryKey: ['product', productId],
      queryFn: async (): Promise<Product | null> => {
        if (!productId || productId.trim() === '') {
          return null
        }

        const { data, error } = await supabase
          .from('products')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            approval_profile:profiles!approved_by(id, full_name),
            current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            product_tags(tags(id, name, color))
          `
          )
          .eq('id', productId)
          .is('deleted_at', null)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch product: ${error.message}`, {
            cause: error,
          })
        }

        return data as Product | null
      },
    }),

  findManyByWorkspace: (
    workspaceId: string,
    filters?: ProductFilters,
    options?: { pageSize?: number }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'products',
        workspaceId,
        filters ?? {},
        options?.pageSize ?? PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0, signal }): Promise<ProductPage> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch products without valid workspace ID'
          )
        }

        const pageSize = options?.pageSize ?? PAGE_SIZE
        const from = pageParam * pageSize
        const to = from + pageSize - 1

        const query = applyProductFilters(
          buildProductSelectQuery(workspaceId, { count: 'exact' }),
          filters
        )

        const { data, count, error } = await query
          .range(from, to)
          .abortSignal(signal)

        if (error) {
          throw new Error(`Failed to fetch products: ${error.message}`, {
            cause: error,
          })
        }

        return {
          products: (data || []) as Product[],
          totalCount: count ?? 0,
        }
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const pageSize = options?.pageSize ?? PAGE_SIZE
        return lastPage.products.length === pageSize
          ? allPages.length
          : undefined
      },
    }),

  findManyCompletedByWorkspace: (
    workspaceId: string,
    filters?: CompletedProductFilters
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'products',
        workspaceId,
        'completed',
        filters ?? {},
        PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0, signal }): Promise<ProductPage> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch products without valid workspace ID'
          )
        }

        const from = pageParam * PAGE_SIZE
        const to = from + PAGE_SIZE - 1

        let query = supabase
          .from('products')
          .select(
            `*,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!inner!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            product_tags(tags(id, name, color))`,
            { count: 'exact' }
          )
          .eq('current_generation.status', 'completed')
          .not('current_generation.storage_path', 'is', null)
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })

        if (filters?.approvalStatus) {
          if (filters.approvalStatus === 'pending') {
            query = query.is('approval_status', null)
          } else {
            query = query.eq('approval_status', filters.approvalStatus)
          }
        }

        if (filters?.searchQuery) {
          for (const word of searchWords(
            filters.searchQuery,
            MIN_SEARCH_WORD_LENGTH
          )) {
            query = query.or(ilikeAnyColumn(['name', 'article_number'], word))
          }
        }

        const { data, count, error } = await query
          .range(from, to)
          .abortSignal(signal)

        if (error) {
          throw new Error(`Failed to fetch products: ${error.message}`, {
            cause: error,
          })
        }

        return {
          products: (data || []) as unknown as Product[],
          totalCount: count ?? 0,
        }
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.products.length === PAGE_SIZE ? allPages.length : undefined,
    }),

  findAllCompletedByWorkspace: (
    workspaceId: string,
    filters?: CompletedProductFilters
  ) =>
    queryOptions({
      queryKey: ['products', workspaceId, 'completed', 'all', filters ?? {}],
      queryFn: async (): Promise<Product[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch products without valid workspace ID'
          )
        }

        let query = supabase
          .from('products')
          .select(
            `*,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!inner!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            product_tags(tags(id, name, color))`
          )
          .eq('current_generation.status', 'completed')
          .not('current_generation.storage_path', 'is', null)
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })

        if (filters?.approvalStatus) {
          if (filters.approvalStatus === 'pending') {
            query = query.is('approval_status', null)
          } else {
            query = query.eq('approval_status', filters.approvalStatus)
          }
        }

        if (filters?.searchQuery) {
          for (const word of searchWords(
            filters.searchQuery,
            MIN_SEARCH_WORD_LENGTH
          )) {
            query = query.or(ilikeAnyColumn(['name', 'article_number'], word))
          }
        }

        const { data, error } = await query

        if (error) {
          throw new Error(`Failed to fetch products: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as unknown as Product[]
      },
    }),

  findFilterOptions: (workspaceId: string) =>
    queryOptions({
      queryKey: ['products', 'filters', workspaceId],
      queryFn: async ({ signal }): Promise<ProductFilterOptions> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch product filters without valid workspace ID'
          )
        }

        const { data, error } = await supabase
          .rpc('get_product_filter_options', { p_workspace_id: workspaceId })
          .abortSignal(signal)

        if (error) {
          throw new Error(`Failed to fetch product filters: ${error.message}`, {
            cause: error,
          })
        }

        const defaults: ProductFilterOptions = {
          colorways: [],
          collections: [],
          genders: [],
          main_groups: [],
          sub_groups: [],
          categories: [],
          attributes: {},
        }

        return data ? (data as unknown as ProductFilterOptions) : defaults
      },
      staleTime: 5 * 60 * 1000,
    }),

  /**
   * Create a new product (returns the product ID for linking generations)
   */
  create: async (input: {
    workspaceId: string
    name: string
    articleNumber?: string | null
    authorId: string
  }): Promise<string> => {
    const { data, error } = await supabase
      .from('products')
      .insert({
        workspace_id: input.workspaceId,
        name: input.name,
        article_number: input.articleNumber ?? null,
        author: input.authorId,
      })
      .select('id')
      .single()

    if (error) {
      throw new Error(`Failed to create product: ${error.message}`, {
        cause: error,
      })
    }

    return data.id
  },

  /**
   * Update product's current generation (called after generation completes)
   */
  updateCurrentGeneration: async (
    productId: string,
    generationId: string
  ): Promise<void> => {
    const { error } = await supabase
      .from('products')
      .update({ current_generation_id: generationId })
      .eq('id', productId)

    if (error) {
      throw new Error(
        `Failed to update product current generation: ${error.message}`,
        { cause: error }
      )
    }
  },

  /**
   * Update product metadata (name, article_number)
   */
  update: async (
    productId: string,
    input: { name?: string; articleNumber?: string | null }
  ): Promise<void> => {
    const { error } = await supabase
      .from('products')
      .update({
        ...(input.name !== undefined && { name: input.name }),
        ...(input.articleNumber !== undefined && {
          article_number: input.articleNumber,
        }),
      })
      .eq('id', productId)

    if (error) {
      throw new Error(`Failed to update product: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Update product approval status
   */
  updateApproval: async (
    productId: string,
    input: {
      approvalStatus: 'approved' | 'denied' | null
      approvalComment: string | null
      approvedBy: string | null
    }
  ): Promise<void> => {
    if (!productId || productId.trim() === '') {
      throw new Error(
        'Invalid productId: cannot update approval without valid ID'
      )
    }

    const { error } = await supabase
      .from('products')
      .update({
        approval_status: input.approvalStatus,
        approval_comment: input.approvalComment,
        approved_by: input.approvedBy,
        approved_at: input.approvalStatus ? new Date().toISOString() : null,
      })
      .eq('id', productId)

    if (error) {
      throw new Error(`Failed to update product approval: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Bulk update approval status for specific product IDs
   */
  bulkUpdateApproval: async (
    productIds: string[],
    input: {
      approvalStatus: 'approved' | 'denied'
      approvedBy: string
    }
  ): Promise<void> => {
    if (!productIds.length) {
      return
    }

    const { error } = await supabase
      .from('products')
      .update({
        approval_status: input.approvalStatus,
        approved_by: input.approvedBy,
        approved_at: new Date().toISOString(),
      })
      .in('id', productIds)

    if (error) {
      throw new Error(
        `Failed to bulk update product approval: ${error.message}`,
        { cause: error }
      )
    }
  },

  bulkUpdateApprovalByWorkspace: async (
    workspaceId: string,
    filters: ProductFilters | undefined,
    input: {
      approvalStatus: 'approved' | 'denied'
      approvedBy: string
    },
    excludeIds?: string[]
  ): Promise<void> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return
    }

    let query = applyProductFilters(
      supabase
        .from('products')
        .update({
          approval_status: input.approvalStatus,
          approved_by: input.approvedBy,
          approved_at: new Date().toISOString(),
        })
        .eq('workspace_id', workspaceId)
        .is('deleted_at', null),
      filters
    )

    if (excludeIds && excludeIds.length > 0) {
      query = query.not('id', 'in', `(${excludeIds.join(',')})`)
    }

    const { error } = await query

    if (error) {
      throw new Error(
        `Failed to bulk update product approval: ${error.message}`,
        { cause: error }
      )
    }
  },

  /**
   * Soft delete a product
   */
  delete: async (productId: string): Promise<void> => {
    if (!productId || productId.trim() === '') {
      throw new Error(
        'Invalid productId: cannot delete product without valid ID'
      )
    }

    const { error } = await supabase
      .from('products')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', productId)

    if (error) {
      throw new Error(`Failed to delete product: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Soft delete multiple products
   */
  deleteMany: async (productIds: string[]): Promise<void> => {
    if (!productIds.length) {
      return
    }

    const { error } = await supabase
      .from('products')
      .update({ deleted_at: new Date().toISOString() })
      .in('id', productIds)

    if (error) {
      throw new Error(`Failed to delete products: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Ids of every product matching the filters, for actions on a select-all
   * selection.
   */
  fetchIdsByWorkspace: async (
    workspaceId: string,
    filters?: ProductFilters
  ): Promise<string[]> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return []
    }

    const rows = await fetchAllRows(
      (from, to) =>
        applyProductFilters(
          supabase
            .from('products')
            .select('id')
            .eq('workspace_id', workspaceId)
            .is('deleted_at', null),
          filters
        )
          .order('id')
          .range(from, to),
      'Failed to fetch product ids'
    )

    return rows.map((row) => row.id)
  },

  fetchAllByWorkspace: async (
    workspaceId: string,
    filters?: ProductFilters
  ): Promise<Product[]> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return []
    }

    const products = await fetchAllRows(
      (from, to) =>
        applyProductFilters(
          buildProductSelectQuery(workspaceId),
          filters
        ).range(from, to),
      'Failed to fetch products'
    )

    return products as Product[]
  },

  deleteAllByWorkspace: async (
    workspaceId: string,
    filters?: ProductFilters,
    excludeIds?: string[]
  ): Promise<void> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return
    }

    let baseQuery = supabase
      .from('products')
      .update({ deleted_at: new Date().toISOString() })
      .eq('workspace_id', workspaceId)
      .is('deleted_at', null)

    baseQuery = applyProductFilters(baseQuery, filters)

    if (excludeIds && excludeIds.length > 0) {
      baseQuery = baseQuery.not('id', 'in', `(${excludeIds.join(',')})`)
    }

    const { error } = await baseQuery

    if (error) {
      throw new Error(`Failed to delete products: ${error.message}`, {
        cause: error,
      })
    }
  },
}

/**
 * Find a product in any workspace infinite query cache.
 * For use in route loaders (non-hook context).
 */
export function findCachedProduct(
  queryClient: QueryClient,
  workspaceId: string,
  productId: string
): Product | null {
  const queries = queryClient.getQueriesData<InfiniteData<ProductPage>>({
    queryKey: ['products', workspaceId],
    exact: false,
  })

  for (const [, data] of queries) {
    // The completed-products list shares this key prefix but is not paged.
    if (!Array.isArray(data?.pages)) {
      continue
    }
    const found = data.pages
      .flatMap((p) => p.products)
      .find((p) => p.id === productId)
    if (found) {
      return found
    }
  }
  return null
}
